import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Activity,
  User,
  Plus,
  CheckCircle,
  Clock,
  Trophy,
  Target,
  ArrowLeft,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import type { SessionDetail, SessionGame } from '../../../shared/session-types';

interface Game {
  id: number;
  nombre: string;
  codigo: string;
  dificultades: Array<{
    id: number;
    nombre: string;
    orden: number;
  }>;
}

interface GameResult {
  sesion_juego_id: number;
  juego_nombre: string;
  dificultad_nombre: string;
  tiempo: number;
  movimientos: number;
  completado: boolean;
  fecha: string;
}

export default function ActivePresencialSessionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [showGameSelector, setShowGameSelector] = useState(false);
  const [selectedGame, setSelectedGame] = useState<number | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<number | null>(null);
  const [results, setResults] = useState<GameResult[]>([]);
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [observacionesFinales, setObservacionesFinales] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSession();
    loadGames();
    loadResults();
  }, [id]);

  const loadSession = async () => {
    try {
      const response = await api.get(`/professional/sessions/${id}`);
      setSession(response.data.data);

      if (response.data.data.estado !== 'en_curso') {
        toast.error('Esta sesión no está activa');
        navigate('/profesional/sesiones');
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Error al cargar la sesión');
      navigate('/profesional/sesiones');
    } finally {
      setLoading(false);
    }
  };

  const loadGames = async () => {
    try {
      const response = await api.get('/professional/games');
      setGames(response.data.data);
    } catch (error) {
      console.error('Error loading games:', error);
    }
  };

  const loadResults = async () => {
    try {
      const response = await api.get(`/professional/sessions/${id}/results`);
      setResults(response.data.data);
    } catch (error) {
      console.error('Error loading results:', error);
    }
  };

  const handleAddGame = async () => {
    if (!selectedGame || !selectedDifficulty) {
      toast.error('Selecciona un juego y dificultad');
      return;
    }

    setSaving(true);
    try {
      const orden = (session?.juegos.length || 0) + 1;

      await api.post(`/professional/sessions/${id}/games`, {
        juego_id: selectedGame,
        dificultad_id: selectedDifficulty,
        orden,
      });

      toast.success('Juego agregado a la sesión');
      setShowGameSelector(false);
      setSelectedGame(null);
      setSelectedDifficulty(null);
      await loadSession();
    } catch (error) {
      console.error('Error adding game:', error);
      toast.error('Error al agregar el juego');
    } finally {
      setSaving(false);
    }
  };

  const handleStartGame = (game: SessionGame) => {
    const gameObj = games.find(g => g.id === game.juego_id);
    if (!gameObj) return;

    // Navegar al juego con parámetros de sesión presencial
    const url = `/juegos/${gameObj.codigo}/${game.dificultad_id}?sesion=${id}&sesion_juego=${game.id}`;
    window.open(url, '_blank');
  };

  const handleFinalizarSesion = async () => {
    setSaving(true);
    try {
      await api.put(`/professional/sessions/${id}`, {
        estado: 'finalizada',
        observaciones_finales: observacionesFinales || null,
      });

      toast.success('Sesión finalizada correctamente');
      navigate('/profesional/sesiones');
    } catch (error) {
      console.error('Error finalizing session:', error);
      toast.error('Error al finalizar la sesión');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!session) return null;

  const selectedGameData = games.find(g => g.id === selectedGame);

  return (
    <div className="min-h-screen pb-20">
      {/* Banner de Sesión Activa */}
      <motion.div
        className="glass-container mb-6 border-2 border-green-500/50 bg-green-500/10"
        animate={{
          boxShadow: [
            '0 0 20px rgba(34, 197, 94, 0.2)',
            '0 0 40px rgba(34, 197, 94, 0.4)',
            '0 0 20px rgba(34, 197, 94, 0.2)',
          ],
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <Activity className="w-6 h-6 text-green-400" />
            </motion.div>
            <div>
              <h2 className="text-xl font-bold text-green-400">
                🟢 Sesión Presencial Activa
              </h2>
              <p className="text-sm text-gray-400">
                Iniciada: {new Date(session.fecha_inicio!).toLocaleString()}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/profesional/sesiones')}
            className="glass-button px-4 py-2 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
        </div>
      </motion.div>

      {/* Información del Paciente */}
      <div className="glass-container mb-6">
        <div className="flex items-center gap-3 mb-4">
          <User className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold">Información del Paciente</h3>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-2xl font-bold">
            {session.paciente_nombre?.[0] || 'P'}
          </div>
          <div>
            <p className="text-lg font-semibold">
              {session.paciente_nombre} {session.paciente_apellidos}
            </p>
            <p className="text-sm text-gray-400">{session.paciente_email}</p>
          </div>
        </div>
        {session.objetivos && (
          <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <p className="text-sm text-blue-300">
              <strong>Objetivos:</strong> {session.objetivos}
            </p>
          </div>
        )}
      </div>

      {/* Estadísticas de la Sesión */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="glass-container">
          <div className="flex items-center gap-3">
            <Target className="w-5 h-5 text-purple-400" />
            <div>
              <p className="text-sm text-gray-400">Juegos Asignados</p>
              <p className="text-2xl font-bold">{session.stats.total_juegos}</p>
            </div>
          </div>
        </div>
        <div className="glass-container">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-sm text-gray-400">Completados</p>
              <p className="text-2xl font-bold">{session.stats.juegos_completados}</p>
            </div>
          </div>
        </div>
        <div className="glass-container">
          <div className="flex items-center gap-3">
            <Trophy className="w-5 h-5 text-yellow-400" />
            <div>
              <p className="text-sm text-gray-400">Progreso</p>
              <p className="text-2xl font-bold">{session.stats.progreso_porcentaje}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Juegos de la Sesión */}
      <div className="glass-container mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Juegos de la Sesión</h3>
          <button
            onClick={() => setShowGameSelector(true)}
            className="glass-button px-4 py-2 flex items-center gap-2 bg-blue-500/20 hover:bg-blue-500/30"
          >
            <Plus className="w-4 h-4" />
            Agregar Juego
          </button>
        </div>

        {session.juegos.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>No hay juegos asignados aún.</p>
            <p className="text-sm mt-2">Haz clic en "Agregar Juego" para comenzar.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {session.juegos.map((game) => (
              <div
                key={game.id}
                className="p-4 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold">{game.juego_nombre}</h4>
                    <p className="text-sm text-gray-400">
                      Dificultad: {game.dificultad_nombre}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        game.estado === 'completado'
                          ? 'bg-green-500/20 text-green-400'
                          : game.estado === 'en_curso'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {game.estado}
                    </span>
                    <button
                      onClick={() => handleStartGame(game)}
                      className="glass-button px-4 py-2"
                    >
                      {game.estado === 'pendiente' ? 'Iniciar' : 'Continuar'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resultados Recientes */}
      {results.length > 0 && (
        <div className="glass-container mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-5 h-5 text-orange-400" />
            <h3 className="text-lg font-semibold">Resultados de la Sesión</h3>
          </div>
          <div className="space-y-2">
            {results.map((result, index) => (
              <div
                key={index}
                className="p-3 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between"
              >
                <div>
                  <p className="font-medium">{result.juego_nombre}</p>
                  <p className="text-sm text-gray-400">
                    {result.dificultad_nombre} • {result.tiempo}s • {result.movimientos} movimientos
                  </p>
                </div>
                {result.completado && (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Botón Finalizar Sesión */}
      <div className="glass-container">
        <button
          onClick={() => setShowFinalizarModal(true)}
          className="w-full glass-button px-6 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 font-semibold"
        >
          <Save className="w-5 h-5 inline mr-2" />
          Finalizar Sesión Presencial
        </button>
      </div>

      {/* Modal: Selector de Juegos */}
      {showGameSelector && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="glass-container max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          >
            <h3 className="text-xl font-bold mb-4">Seleccionar Juego</h3>

            <div className="space-y-4 mb-6">
              {/* Selector de Juego */}
              <div>
                <label className="block text-sm font-medium mb-2">Juego</label>
                <select
                  value={selectedGame || ''}
                  onChange={(e) => {
                    setSelectedGame(Number(e.target.value));
                    setSelectedDifficulty(null);
                  }}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-blue-500"
                >
                  <option value="">Selecciona un juego...</option>
                  {games.map((game) => (
                    <option key={game.id} value={game.id}>
                      {game.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Selector de Dificultad */}
              {selectedGameData && (
                <div>
                  <label className="block text-sm font-medium mb-2">Dificultad</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedGameData.dificultades.map((dif) => (
                      <button
                        key={dif.id}
                        onClick={() => setSelectedDifficulty(dif.id)}
                        className={`p-3 rounded-lg border transition-all ${
                          selectedDifficulty === dif.id
                            ? 'bg-blue-500/30 border-blue-500'
                            : 'bg-white/5 border-white/10 hover:bg-white/10'
                        }`}
                      >
                        {dif.nombre}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowGameSelector(false);
                  setSelectedGame(null);
                  setSelectedDifficulty(null);
                }}
                className="flex-1 glass-button px-4 py-2"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleAddGame}
                disabled={!selectedGame || !selectedDifficulty || saving}
                className="flex-1 glass-button px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Agregando...' : 'Agregar a Sesión'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal: Finalizar Sesión */}
      {showFinalizarModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="glass-container max-w-lg w-full"
          >
            <h3 className="text-xl font-bold mb-4 text-red-400">
              ⚠️ Finalizar Sesión Presencial
            </h3>

            <p className="text-gray-300 mb-4">
              ¿Estás seguro de que deseas finalizar esta sesión? Esta acción no se puede deshacer.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                Observaciones Finales (opcional)
              </label>
              <textarea
                value={observacionesFinales}
                onChange={(e) => setObservacionesFinales(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
                placeholder="Añade observaciones sobre la sesión..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowFinalizarModal(false)}
                className="flex-1 glass-button px-4 py-2"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleFinalizarSesion}
                disabled={saving}
                className="flex-1 glass-button px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 disabled:opacity-50"
              >
                {saving ? 'Finalizando...' : 'Finalizar Sesión'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
